import '.ab{position:relative;}.ssr-plugin'
import '.lx{opacity:0.5;}.ssr-plugin'
import '.bx{position:absolute;}.ssr-plugin'
import '.jh{inset:0px;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.ax{justify-content:center;}.ssr-plugin'
import '.cm{pointer-events:none;}.ssr-plugin'
import '.c4{text-decoration:none;}.ssr-plugin'
import '.u{gap:0.5rem;}.ssr-plugin'
import '.ly{padding:0.5rem 0.75rem;}.ssr-plugin'
import '.fg{border-radius:0.5rem;}.ssr-plugin'
import '.i3{border:1px solid var(--gray-200);}.ssr-plugin'
import '.v{background-color:var(--palette-white);}.ssr-plugin'
import '.lz{opacity:0.6;}.ssr-plugin'
import '.m0{margin-left:2.5rem;}.ssr-plugin'
import '.m1{padding-top:0.5rem;}.ssr-plugin'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Text } from '@app/components/core/typography/Text'
import { TextLink } from '@app/components/core/link/TextLink'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { PostActivityRow } from './PostActivityRow'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthState } from '@based/sdk/react'
import { PostMediaGallery } from '../../mediaGallery/PostMediaGallery'
import { FileDisplay } from '@app/components/core/fileDisplay/FileDisplay'
import { styled } from 'inlines'
import { useRelativeDateFormat } from '@app/util/useDateFormat'
import { PostProps } from './models'
import { ReplyTextBox } from './ReplyTextBox'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { LoadMoreCommentsButton } from './LoadMoreButton'
import { PostContent } from './PostContent'
import { pages } from '@app/util/links'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'

export type CommentProps = Omit<PostProps, 'onLoadMoreComments'> & {
  level: 'comment' | 'reply'
  loadMoreComments?: () => void
}

export const Comment = ({
  level,
  post: {
    id,
    authorName,
    authorAvatar,
    authorHandle,
    creationDate,
    content,
    media,
    reactionUserIds,
    comments,
  },
  onReply,
  onReact,
  loadMoreComments,
}: CommentProps) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.post
  const authState = useAuthState()
  const [replyInputShown, setReplyInputShown] = useState(false)
  const [mounted, setMounted] = useState(false)

  const postReplyRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  const hasReaction = !!(
    mounted &&
    authState.userId &&
    reactionUserIds.includes(authState.userId.toString())
  )
  const relativeDate = useRelativeDateFormat()

  const onReplyHandler = useCallback(() => {
    setReplyInputShown(true)
    if (postReplyRef.current) {
      postReplyRef.current.focus()
    }
  }, [postReplyRef])

  const onSubmitHandler = useCallback(
    (text: string, files: File[]) => {
      setReplyInputShown(false)
      return onReply(id, text, files)
    },
    [id, onReply],
  )

  useEffect(() => {
    if (replyInputShown && postReplyRef.current) {
      postReplyRef.current.focus()
    }
  }, [replyInputShown])

  return (
    <Column
      gap="0.5rem"
      alignItems="stretch"
      style={{
        paddingTop: '1rem',
      }}
    >
      <Column
        gap="0.25rem"
        style={{ marginBottom: '0.5rem' }}
        alignItems="stretch"
      >
        <Row
          gap="0.5rem"
          style={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Row gap="0.5rem" style={{ alignItems: 'center' }}>
            {authorHandle ? (
              <TextLink
                to={pages.profile(authorHandle)}
                target="_blank"
                containerStyle={{ textDecoration: 'none', display: 'inline-flex' }}
              >
                <Avatar
                  size="extra-small"
                  image={authorAvatar}
                  labelSource={authorName}
                />
              </TextLink>
            ) : (
              <Avatar
                size="extra-small"
                image={authorAvatar}
                labelSource={authorName}
              />
            )}
            {authorHandle ? (
              <TextLink
                to={pages.profile(authorHandle)}
                target="_blank"
                isBold={true}
                textStyle={{ color: 'var(--brand-secondary)' }}
                containerStyle={{ textDecoration: 'none' }}
              >
                {authorName}
              </TextLink>
            ) : (
              <Text.Medium isBold={true}>{authorName}</Text.Medium>
            )}
          </Row>
          <Text.Small style={{ color: 'var(--gray-300)' }}>
            {relativeDate(creationDate)}
          </Text.Small>
        </Row>
        <Column
          alignItems="stretch"
          style={{ marginLeft: '2.5rem', gap: '0.62rem' }}
        >
          <PostContent content={content} />
          <PostMediaGallery
            images={media
              .filter((m) => m.mimeType !== 'application/pdf' && !m.uploading)
              .map((m) => m.src)}
          />
          {media.filter((m) => m.mimeType !== 'application/pdf' && m.uploading)
            .length > 0 && (
            <div className="ab">
              <div className="lx">
                <PostMediaGallery
                  images={media
                    .filter((m) => m.mimeType !== 'application/pdf' && m.uploading)
                    .map((m) => m.src)}
                />
              </div>
              <div className="bx jh a t ax cm"
              >
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{
                    color: 'var(--brand-secondary)',
                    fontSize: '1.5rem',
                    filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8))',
                  }}
                />
              </div>
            </div>
          )}
          {media.filter((m) => m.mimeType === 'application/pdf' && !m.uploading)
            .length > 0 && (
            <Column gap="0.5rem" alignItems="stretch">
              {media
                .filter((m) => m.mimeType === 'application/pdf' && !m.uploading)
                .map((doc) => (
                  <a className="c4"
                    key={doc.src}
                    href={doc.src}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileDisplay
                      type="document"
                      name={
                        doc.name ?? doc.src.split('/').pop() ?? 'document.pdf'
                      }
                      src={doc.src}
                    />
                  </a>
                ))}
            </Column>
          )}
          {media.filter((m) => m.mimeType === 'application/pdf' && m.uploading)
            .length > 0 && (
            <Column gap="0.5rem" alignItems="stretch">
              {media
                .filter((m) => m.mimeType === 'application/pdf' && m.uploading)
                .map((doc) => (
                  <div className="a t u ly fg i3 v lz"
                    key={doc.name}
                  >
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      style={{
                        color: 'var(--brand-secondary)',
                        fontSize: '0.875rem',
                        flexShrink: 0,
                      }}
                    />
                    <Text.Small
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'var(--brand-secondary)',
                      }}
                    >
                      {doc.name ?? 'document.pdf'}
                    </Text.Small>
                  </div>
                ))}
            </Column>
          )}
          <PostActivityRow
            version={level}
            hasReaction={hasReaction}
            reactions={reactionUserIds.length}
            comments={comments.length}
            onReactionClick={() => onReact(id)}
            onReplyClick={onReplyHandler}
          />
        </Column>
      </Column>
      <div className="m0">
        {level === 'comment' &&
          comments.map((comment, index) => (
            <Comment
              key={index}
              post={comment}
              level="reply"
              onReact={onReact}
              onReply={onReply}
            />
          ))}
        {level === 'comment' && loadMoreComments && (
          <LoadMoreCommentsButton
            label={translations.loadMoreReplies}
            onClick={loadMoreComments}
          />
        )}
      </div>

      {replyInputShown && (
        <div className="m1 m0">
          <ReplyTextBox
            ref={postReplyRef}
            onSumbit={onSubmitHandler}
            onBlur={() => setReplyInputShown(false)}
          />
        </div>
      )}
    </Column>
  )
}
